export interface IcsDay {
  date: Date
  isWorkDay: boolean
}

const pad = (value: number) => String(value).padStart(2, '0')

/** Format a date as a floating calendar date (YYYYMMDD) in local time. */
function toIcsDate(date: Date): string {
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`
}

/** Stamp for DTSTAMP, always UTC. */
function toUtcStamp(date: Date): string {
  return (
    `${date.getUTCFullYear()}${pad(date.getUTCMonth() + 1)}${pad(date.getUTCDate())}` +
    `T${pad(date.getUTCHours())}${pad(date.getUTCMinutes())}${pad(date.getUTCSeconds())}Z`
  )
}

/** Escape text values per RFC 5545, and fold long lines at 75 octets. */
function escapeText(value: string): string {
  return value
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n')
}

function foldLine(line: string): string {
  if (line.length <= 74) return line
  const parts: string[] = [line.slice(0, 74)]
  let rest = line.slice(74)
  while (rest.length > 0) {
    parts.push(` ${rest.slice(0, 73)}`)
    rest = rest.slice(73)
  }
  return parts.join('\r\n')
}

/**
 * Build an iCalendar (ICS) feed where every work day becomes an all-day event,
 * so a roster can be dropped into Outlook, Google Calendar or Apple Calendar.
 */
export function buildRosterIcs(options: {
  days: IcsDay[]
  patternLabel: string
  startDate: string
  monthsToShow: number
  calendarName?: string
}): string {
  const { days, patternLabel, startDate, monthsToShow } = options
  const calendarName = options.calendarName || 'Shift Roster'
  const stamp = toUtcStamp(new Date())

  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Roster Calculator//Shift Roster//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    `X-WR-CALNAME:${escapeText(calendarName)}`,
    `X-WR-CALDESC:${escapeText(`Shift roster for ${patternLabel} starting ${startDate}, ${monthsToShow} month(s).`)}`,
  ]

  days.forEach((day, index) => {
    if (!day.isWorkDay) return

    const start = new Date(day.date)
    start.setHours(0, 0, 0, 0)
    const end = new Date(start)
    end.setDate(end.getDate() + 1)

    lines.push(
      'BEGIN:VEVENT',
      `UID:roster-${toIcsDate(start)}-${index}@rostercalculator.com`,
      `DTSTAMP:${stamp}`,
      `DTSTART;VALUE=DATE:${toIcsDate(start)}`,
      `DTEND;VALUE=DATE:${toIcsDate(end)}`,
      foldLine(`SUMMARY:${escapeText('Shift day')}`),
      foldLine(`DESCRIPTION:${escapeText(`Work day on the ${patternLabel} roster.`)}`),
      'TRANSP:TRANSPARENT',
      'END:VEVENT'
    )
  })

  lines.push('END:VCALENDAR')
  return lines.join('\r\n')
}

/** Trigger a browser download of the generated calendar file. */
export function downloadRosterIcs(fileName: string, content: string): void {
  const blob = new Blob([content], { type: 'text/calendar;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName.endsWith('.ics') ? fileName : `${fileName}.ics`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
